import React, { useCallback, useEffect, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, X, Trash2, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import { Card, CardContent } from '../../../components/new-components/ui/card';
import { Button } from '../../../components/new-components/ui/button';
import { Input } from '../../../components/new-components/ui/input';

interface CohortOption { key: string; label: string; description?: string }
interface CohortMeta {
  applied: string;
  label?: string | null;
  description?: string | null;
  matched: number;
  censusTotal: number;
  available: CohortOption[];
}

export interface EntityColumn {
  key: string;
  label: string;
  format?: (value: any, row: any) => React.ReactNode;
}

interface AdminEntityListProps {
  entity: string;
  title: string;
  columns: EntityColumn[];
  searchPlaceholder?: string;
}

const fmtValue = (v: any): React.ReactNode => {
  if (v === null || v === undefined || v === '') return <span className="text-muted-foreground">—</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="text-muted-foreground">—</span>;
  return String(v);
};

export default function AdminEntityList({ entity, title, columns, searchPlaceholder }: AdminEntityListProps) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<{ page: number; pageSize: number; total: number } | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  // undefined = "use the server's default"; the server decides which cohort a
  // list opens on, so a new entity does not need a matching change here.
  const [cohort, setCohort] = useState<string | undefined>(undefined);
  const [cohortMeta, setCohortMeta] = useState<CohortMeta | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (search) qp.set('q', search);
      if (cohort) qp.set('cohort', cohort);
      const res: any = await api.get(`/emerge/${entity}?${qp.toString()}`);
      setRows(res?.data || []);
      setPagination(res?.pagination || null);
      setCohortMeta(res?.cohort || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entity, page, search, cohort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setCohort(undefined); setCohortMeta(null); }, [entity]);

  const remove = async (id: string) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/emerge/${entity}/${id}`);
      setSelected(null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-[#242424]">{title}</h1>
          {/* Always show the filtered count against the census. Filtering hides
              records; it must never read as though they were deleted. */}
          <p className="text-sm text-muted-foreground">
            {cohortMeta
              ? `${cohortMeta.matched.toLocaleString()} of ${cohortMeta.censusTotal.toLocaleString()} records`
              : pagination ? `${pagination.total.toLocaleString()} records` : ''}
          </p>
          {cohortMeta?.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{cohortMeta.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cohortMeta && cohortMeta.available.length > 1 && (
            <div className="flex items-center rounded-md border border-input overflow-hidden">
              {cohortMeta.available.map((opt) => {
                const active = cohortMeta.applied === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    title={opt.description}
                    onClick={() => { setCohort(opt.key); setPage(1); }}
                    className={`px-3 py-1.5 text-xs whitespace-nowrap transition-colors ${
                      active
                        ? 'bg-[#242424] text-white'
                        : 'bg-transparent text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={searchPlaceholder || 'Search...'}
              className="pl-9 w-64"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border">
                {columns.map((c) => (
                  <th key={c.key} className="text-left p-3 text-xs text-muted-foreground">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border hover:bg-muted/50 cursor-pointer"
                  onClick={() => setSelected(row)}
                >
                  {columns.map((c) => (
                    <td key={c.key} className="p-3 text-sm">
                      {c.format ? c.format(row[c.key], row) : fmtValue(row[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={columns.length} className="p-8 text-center text-sm text-muted-foreground">No records found</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {pagination.page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onMouseDown={() => setSelected(null)}>
          <aside
            className="bg-white dark:bg-[#1a1a1a] w-full max-w-md h-full overflow-y-auto p-6 shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{title.replace(/s$/, '')} record</h2>
              <button onClick={() => setSelected(null)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-col gap-3">
              {Object.entries(selected).map(([k, v]) => (
                <div key={k} className="flex flex-col gap-0.5 border-b border-border pb-2">
                  <span className="text-xs text-muted-foreground">{k}</span>
                  <span className="text-sm break-words">{fmtValue(v)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                disabled={deleting}
                onClick={() => remove(selected.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
